/**
 * Google Drive Authentication Service
 * 
 * Handles OAuth flow, authentication, and connection management for Google Drive
 */

import api from '../api'

class GoogleDriveAuthService {
  /**
   * Get Google Drive OAuth URL
   */
  getAuthUrl() {
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 GoogleDriveAuthService: Onboarding detected, skipping OAuth URL generation')
      throw new Error('OAuth URL generation not available during onboarding')
    }
    
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/google/callback`)
    const scope = encodeURIComponent('https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email')
    
    // Validate client ID
    if (!clientId || clientId === 'your-google-client-id' || !clientId.includes('.apps.googleusercontent.com')) {
      throw new Error('Google Client ID is not properly configured')
    }
    
    // Get current user info to pass through OAuth state
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    console.log('🔍 GoogleDriveAuthService: Generating OAuth state...')
    console.log('🔍 GoogleDriveAuthService: authToken exists:', !!authToken)
    console.log('🔍 GoogleDriveAuthService: user exists:', !!user)
    console.log('🔍 GoogleDriveAuthService: All localStorage keys:', Object.keys(localStorage))
    
    // Try to get user info from JWT if user key is missing
    let userEmail = null
    if (!user && authToken) {
      console.log('🔍 GoogleDriveAuthService: User key missing, attempting to decode JWT token...')
      try {
        const tokenParts = authToken.split('.')
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]))
          console.log('🔍 GoogleDriveAuthService: Decoded JWT payload:', payload)
          if (payload.email) {
            userEmail = payload.email
            console.log('✅ GoogleDriveAuthService: Found user email in JWT:', userEmail)
          }
        }
      } catch (jwtError) {
        console.warn('⚠️ GoogleDriveAuthService: Failed to decode JWT token:', jwtError)
      }
    }
    
    let stateParam = ''
    if (authToken && (user || userEmail)) {
      try {
        let userData
        if (user) {
          userData = JSON.parse(user)
        } else {
          // Create user data from JWT
          userData = { email: userEmail }
        }
        
        console.log('🔍 GoogleDriveAuthService: Using user data:', userData)
        
        const stateData = {
          userEmail: userData.email,
          timestamp: Date.now(),
          sessionId: btoa(authToken.substring(0, 32)).substring(0, 16) // Create a session identifier
        }
        const stateJson = JSON.stringify(stateData)
        stateParam = `&state=${encodeURIComponent(stateJson)}`
        console.log('🔍 GoogleDriveAuthService: Generated state data:', stateData)
        console.log('🔍 GoogleDriveAuthService: State JSON:', stateJson)
        console.log('🔍 GoogleDriveAuthService: Generated state param:', stateParam)
        console.log('🔍 GoogleDriveAuthService: State param length:', stateParam.length)
      } catch (error) {
        console.warn('⚠️ GoogleDriveAuthService: Failed to parse user data for OAuth state:', error)
        console.error('⚠️ GoogleDriveAuthService: Raw user data:', user)
      }
    } else {
      console.warn('⚠️ GoogleDriveAuthService: Missing auth data for OAuth state')
      console.warn('⚠️ GoogleDriveAuthService: authToken:', !!authToken, 'user:', !!user, 'userEmail from JWT:', !!userEmail)
    }
    
    return `https://accounts.google.com/o/oauth2/v2/auth?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&access_type=offline&prompt=consent&include_granted_scopes=false${stateParam}`
  }

  /**
   * Authenticate with Google Drive using authorization code
   */
  async authenticate(authorizationCode) {
    // CRITICAL: Check if user is authenticated before authenticating with Google Drive
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 GoogleDriveAuthService: User not authenticated, blocking Google Drive authentication')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    // Skip operations during onboarding to prevent unnecessary API calls
    const isOnboarding = window.location.pathname.includes('/onboarding')
    if (isOnboarding) {
      console.log('🔒 GoogleDriveAuthService: Onboarding detected, skipping Google Drive authentication')
      throw new Error('Google Drive authentication not available during onboarding')
    }
    
    try {
      console.log('🌐 GoogleDriveAuthService: Authenticating with Google Drive')

      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'
      const response = await fetch(`${API_BASE_URL}/api/cloud/google-drive/auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: authorizationCode }),
      })

      if (!response.ok) {
        throw new Error('Google Drive authentication failed')
      }

      const data = await response.json()
      console.log('✅ GoogleDriveAuthService: Google Drive authentication successful')
      
      return { success: true, data }

    } catch (error) {
      console.error('❌ GoogleDriveAuthService: Google Drive authentication error:', error)
      throw error
    }
  }

  /**
   * Connect to Google Drive (start OAuth flow)
   */
  async connect() {
    // CRITICAL: Check if user is authenticated before connecting
    const authToken = localStorage.getItem('authToken')
    const user = localStorage.getItem('user')
    
    if (!authToken || !user) {
      console.log('🔒 GoogleDriveAuthService: User not authenticated, blocking Google Drive connection')
      throw new Error('User not authenticated. Please log in first.')
    }
    
    try {
      console.log('🔄 GoogleDriveAuthService: Connecting to Google Drive...')
      
      // Skip connection during onboarding to prevent unnecessary API calls
      const isOnboarding = window.location.pathname.includes('/onboarding')
      if (isOnboarding) {
        console.log('🔒 GoogleDriveAuthService: Onboarding detected, skipping Google Drive connection')
        throw new Error('Google Drive connection not available during onboarding')
      }
      
      // Start OAuth flow
      const authUrl = this.getAuthUrl()
      window.location.href = authUrl
      
    } catch (error) {
      console.error('❌ GoogleDriveAuthService: Failed to connect to Google Drive:', error)
      throw error
    }
  }
}

export default new GoogleDriveAuthService()
